// Credibility scorer — DETERMINISTIC STUB.
//
// Wave 2 ships news cards before the Inference orchestrator publishes a real
// source-credibility artifact (per Iron Rule #12: hot-path AI signals come
// from precomputed nightly artifacts, never inline LLM calls).
//
// Until the real artifact lands, this file hashes `source.name + title.length`
// into a stable 0..100 score. Same input → same score, so screenshots, tests
// and demos are reproducible. Real scorer will:
//
//   * weight per-source historical accuracy (decay-windowed),
//   * crosscheck against multi-source corroboration,
//   * down-weight on retraction signals,
//   * lift on byline + dateline presence.
//
// All of that lives in the inference gateway — NOT here. Do not extend this
// file with anything that isn't a deterministic primitive.

const KNOWN_HIGH = new Set([
  'reuters',
  'associated press',
  'ap',
  'bbc',
  'afp',
  'agence france-presse',
  'al jazeera',
  'defense news',
  'janes',
  "jane's",
  'isw',
  'institute for the study of war',
]);

const KNOWN_MED = new Set([
  'cnn',
  'the guardian',
  'guardian',
  'nyt',
  'new york times',
  'washington post',
  'bloomberg',
  'financial times',
  'ft',
  'wsj',
  'wall street journal',
  'le monde',
  'der spiegel',
]);

/**
 * FNV-1a 32-bit hash. Tiny, fast, deterministic across JS engines.
 * We use the low 7 bits to spread the score over 0..127 then clamp to 100.
 */
function fnv1a(input: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h;
}

/**
 * Score a candidate news item 0..100. Deterministic on `sourceName + titleLength`.
 *
 *  * Known-high outlets land 80..95.
 *  * Known-medium outlets land 60..79.
 *  * Everything else lands 30..69.
 *
 * The spread inside each band is hash-derived so two stub stories from the
 * same outlet don't collide on the same score.
 */
export function credibilityScore(sourceName: string, titleLength: number): number {
  const key = `${sourceName.toLowerCase()}|${titleLength}`;
  const h = fnv1a(key);
  const lo = sourceName.toLowerCase().trim();
  const variance = h & 0x0f; // 0..15
  if (KNOWN_HIGH.has(lo)) return 80 + variance; // 80..95
  if (KNOWN_MED.has(lo)) return 60 + variance + 4; // 64..79
  return 30 + (h & 0x1f) + 8; // 38..69 (roughly)
}

/**
 * Human-facing band label for the credibility chip.
 *   ≥ 80 → HIGH, ≥ 60 → MED, ≥ 40 → LOW, else WEAK.
 */
export function credibilityBand(score: number): 'HIGH' | 'MED' | 'LOW' | 'WEAK' {
  if (score >= 80) return 'HIGH';
  if (score >= 60) return 'MED';
  if (score >= 40) return 'LOW';
  return 'WEAK';
}
